import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import Modal from 'shared/ui/Modal';
import './FanInsightsModal.css';

const normalizeInsights = (payload) => payload?.data || payload?.result || payload || null;

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const normalizeCurrency = (currency) => {
  const code = typeof currency === 'string' ? currency.toUpperCase() : 'EUR';
  return /^[A-Z]{3}$/.test(code) ? code : 'EUR';
};

const formatCurrency = (amount, currency = 'EUR') => {
  const number = toFiniteNumber(amount);
  if (number === null) return '-';

  try {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: normalizeCurrency(currency),
    }).format(number);
  } catch (_) {
    return `${number} ${normalizeCurrency(currency)}`;
  }
};

const formatNumber = (value) => {
  const number = toFiniteNumber(value);
  return number === null ? '-' : new Intl.NumberFormat('fr-FR').format(number);
};

const formatPercent = (value) => {
  const number = toFiniteNumber(value);
  return number === null ? '-' : `${new Intl.NumberFormat('fr-FR').format(number)}%`;
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getExpenseCurrency = (expenses) => {
  const primary = Array.isArray(expenses)
    ? expenses.find((expense) => toFiniteNumber(expense?.amount) !== null)
    : null;
  return normalizeCurrency(primary?.currency);
};

const formatExpenses = (expenses) => {
  if (!Array.isArray(expenses) || expenses.length === 0) return '-';

  const totalsByCurrency = expenses.reduce((acc, expense) => {
    const amount = toFiniteNumber(expense?.amount);
    if (amount === null) return acc;
    const currency = normalizeCurrency(expense?.currency);
    acc[currency] = (acc[currency] || 0) + amount;
    return acc;
  }, {});

  const entries = Object.entries(totalsByCurrency);
  if (entries.length === 0) return '-';
  return entries.map(([currency, amount]) => formatCurrency(amount, currency)).join(' + ');
};

const FanInsightsModal = ({ isOpen, onClose, conversation }) => {
  const [insights, setInsights] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setInsights(null);
      setError(null);
      setIsLoading(false);
      return undefined;
    }

    setInsights(null);

    if (!conversation?.fanId || !conversation?.subscriptionId) {
      setError("Impossible de charger les infos de ce fan.");
      return undefined;
    }

    let isCancelled = false;

    const fetchInsights = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const jwt = Cookies.get('user_token');
        const response = await axios.post(
          'https://unbot.io/api/v1.0/proxy',
          {
            method: 'GET',
            path: 'v2.0/users/expenses/chat',
            parameters: { fanId: conversation.fanId },
            subscriptionId: conversation.subscriptionId,
          },
          { headers: { Authorization: `Bearer ${jwt}` } }
        );

        if (!isCancelled) {
          setInsights(normalizeInsights(response.data));
        }
      } catch (err) {
        console.error('Error fetching fan insights:', err);
        if (!isCancelled) {
          setError("Impossible de charger les infos de ce fan.");
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchInsights();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, conversation?.fanId, conversation?.subscriptionId]);

  const currency = useMemo(
    () => getExpenseCurrency(insights?.expenses),
    [insights?.expenses]
  );
  const photoPercent = toFiniteNumber(insights?.mediaTypeBought?.photo);
  const videoPercent = toFiniteNumber(insights?.mediaTypeBought?.video);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Infos fan"
      size="small"
      overlayClassName="fan-insights-overlay"
      panelClassName="fan-insights-panel"
      headerClassName="fan-insights-header"
      bodyClassName="fan-insights-body"
      closeButtonClassName="fan-insights-close"
    >
      {isLoading ? (
        <div className="fan-insights-state">Chargement des infos...</div>
      ) : error ? (
        <div className="fan-insights-state error">{error}</div>
      ) : insights ? (
        <div className="fan-insights-shell">
          <div className="fan-insights-core">
            <div className="fan-insights-hero">
              <span>Total dépensé</span>
              <strong>{formatExpenses(insights.expenses)}</strong>
              <small>{formatNumber(insights.mediaBought)} médias achetés</small>
            </div>

            <div className="fan-insights-grid">
              <div className="fan-insights-card">
                <span>Tips</span>
                <strong>{formatNumber(insights.tips)}</strong>
              </div>
              <div className="fan-insights-card">
                <span>Médias envoyés</span>
                <strong>{formatNumber(insights.mediaSentCount)}</strong>
              </div>
              <div className="fan-insights-card">
                <span>Taux d'achat</span>
                <strong>{formatPercent(insights.purchaseRate)}</strong>
              </div>
              <div className="fan-insights-card">
                <span>Dernier achat</span>
                <strong>{formatCurrency(insights.lastPurchase?.price, currency)}</strong>
                <small>{formatDate(insights.lastPurchase?.date)}</small>
              </div>
            </div>

            <div className="fan-insights-split">
              <div>
                <span>Photos</span>
                <strong>{formatPercent(photoPercent)}</strong>
                <div className="fan-insights-bar">
                  <i style={{ '--fan-insights-value': `${photoPercent || 0}%` }} />
                </div>
              </div>
              <div>
                <span>Vidéos</span>
                <strong>{formatPercent(videoPercent)}</strong>
                <div className="fan-insights-bar">
                  <i style={{ '--fan-insights-value': `${videoPercent || 0}%` }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </Modal>
  );
};

export default FanInsightsModal;
